import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { OC, FONT } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import { isEmployerRole } from '../constants/index.js';
import OCApi from '../api/client.js';
import { Button, Icon, Input, Card } from '../components/ui.js';
import { ChangePasswordModal, SwitchAccountTypeModal, DeleteAccountModal } from '../components/modals.js';

// --- AccountSettingsSection ---
function AccountSettingsSection() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [name, setName] = useState(s.user.name);
    const [showPwModal, setShowPwModal] = useState(false);
    const [showSwitchModal, setShowSwitchModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const nameChanged = name.trim() !== s.user.name && name.trim().length > 1;

    const saveName = async () => {
        try {
            await OCApi.updateProfile({ displayName: name.trim() });
            s.setUser(u => ({ ...u, name: name.trim() }));
            s.showToast('Display name updated', 'check');
        } catch (err) {
            s.showToast(err.message || 'Could not update name', 'x');
        }
    };

    return React.createElement('div', { style: { width: '100%', padding: '0 40px' } },
        React.createElement('div', { style: { padding: '40px 0 80px' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: t.text.secondary, marginBottom: 12 } },
                React.createElement('button', { onClick: () => s.go('home'), style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary, fontSize: 12.5, padding: 0 } }, 'Home'),
                React.createElement(Icon, { name: 'chevRight', size: 12 }),
                React.createElement('span', { style: { color: t.text.primary, fontWeight: 600 } }, 'Settings')
            ),
            React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 32, margin: '0 0 24px', color: t.text.primary, letterSpacing: '-0.02em' } }, 'Account settings'),
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 18, width: '100%' } },
                React.createElement(Card, { theme: s.theme, padding: 22 },
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' } },
                        React.createElement('div', null,
                            React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary, marginBottom: 4 } }, 'Email alerts'),
                            React.createElement('div', { style: { fontSize: 13, color: t.text.secondary } },
                                s.user.alertsPaused
                                    ? 'All saved-search alert emails are paused for your account.'
                                    : 'You will receive saved-search alert emails when new matches are published.'
                            )
                        ),
                        React.createElement(Button, {
                            kind: s.user.alertsPaused ? 'primary' : 'secondary',
                            size: 'md',
                            theme: s.theme,
                            icon: React.createElement(Icon, { name: 'bell', size: 15 }),
                            onClick: () => s.setAlertsPaused(!s.user.alertsPaused),
                        }, s.user.alertsPaused ? 'Resume all alerts' : 'Pause all alerts')
                    )
                ),
                React.createElement(Card, { theme: s.theme, padding: 22 },
                    React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary, marginBottom: 4 } }, 'Display name'),
                    React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginBottom: 14 } }, 'This is the name shown on your profile and applications.'),
                    React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'flex-end' } },
                        React.createElement('div', { style: { flex: 1 } },
                            React.createElement(Input, { theme: s.theme, value: name, onChange: setName, placeholder: 'Your name', icon: 'user' })
                        ),
                        React.createElement(Button, {
                            kind: 'primary', size: 'md', theme: s.theme, onClick: saveName,
                            style: { opacity: nameChanged ? 1 : 0.5, cursor: nameChanged ? 'pointer' : 'not-allowed', pointerEvents: nameChanged ? 'auto' : 'none', height: 44 }
                        }, 'Save name')
                    )
                ),
                React.createElement(Card, { theme: s.theme, padding: 22 },
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' } },
                        React.createElement('div', null,
                            React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary, marginBottom: 4 } }, 'Password'),
                            React.createElement('div', { style: { fontSize: 13, color: t.text.secondary } }, 'Keep your account secure with a strong password.')
                        ),
                        React.createElement(Button, { kind: 'secondary', size: 'md', theme: s.theme, icon: React.createElement(Icon, { name: 'lock', size: 15 }), onClick: () => setShowPwModal(true) }, 'Change password')
                    )
                ),
                React.createElement(Card, { theme: s.theme, padding: 22 },
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' } },
                        React.createElement('div', null,
                            React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary, marginBottom: 4 } },
                                isEmployerRole(s.user.role) ? 'Employer account active' : 'Switch to Hiring Employer'
                            ),
                            React.createElement('div', { style: { fontSize: 13, color: t.text.secondary } },
                                isEmployerRole(s.user.role)
                                    ? 'You can post roles and manage applicants from the employer dashboard.'
                                    : 'Post roles and manage applicants under an employer profile.'
                            )
                        ),
                        isEmployerRole(s.user.role) ?
                            React.createElement(Button, { kind: 'accentSubtle', size: 'md', theme: s.theme, icon: React.createElement(Icon, { name: 'chart', size: 15 }), onClick: () => s.go('employers') }, 'Open dashboard') :
                            React.createElement(Button, { kind: 'accentSubtle', size: 'md', theme: s.theme, icon: React.createElement(Icon, { name: 'briefcase', size: 15 }), onClick: () => setShowSwitchModal(true) }, 'Switch account type')
                    )
                ),
                React.createElement(Card, { theme: s.theme, padding: 22, style: { border: `1px solid ${OC.urgent}33` } },
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' } },
                        React.createElement('div', null,
                            React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: OC.urgent, marginBottom: 4 } }, 'Delete account'),
                            React.createElement('div', { style: { fontSize: 13, color: t.text.secondary } }, 'Permanently remove your account and all saved data. This cannot be undone.')
                        ),
                        React.createElement(Button, { kind: 'destructive', size: 'md', onClick: () => setShowDeleteModal(true) }, 'Delete my account')
                    )
                ),
                showPwModal ? React.createElement(ChangePasswordModal, { onClose: () => setShowPwModal(false) }) : null,
                showSwitchModal ? React.createElement(SwitchAccountTypeModal, { onClose: () => setShowSwitchModal(false) }) : null,
                showDeleteModal ? React.createElement(DeleteAccountModal, { onClose: () => setShowDeleteModal(false) }) : null
            )
        )
    );
}

export { AccountSettingsSection };
